import { supabase } from '@/supabase';
import type { Provider, Session, User } from '@supabase/supabase-js';

import type {
  AppAuthSession,
  AppAuthUser,
  AuthChangeEvent,
  AuthMetadata,
  AuthSubscription,
  OAuthProvider,
  SessionResult,
  SignInWithPasswordResult,
  SignUpResult,
  UserResult,
} from './types';

const toAppUser = (user: User | null): AppAuthUser | null => {
  if (!user) return null;

  return {
    id: user.id,
    email: user.email,
    emailConfirmedAt: user.email_confirmed_at,
    email_confirmed_at: user.email_confirmed_at,
    userMetadata: (user.user_metadata ?? {}) as AuthMetadata,
    user_metadata: (user.user_metadata ?? {}) as AuthMetadata,
    appMetadata: (user.app_metadata ?? {}) as AuthMetadata,
    app_metadata: (user.app_metadata ?? {}) as AuthMetadata,
  };
};

const toAppSession = (session: Session | null): AppAuthSession | null => {
  if (!session) return null;
  const user = toAppUser(session.user);
  if (!user) return null;

  return {
    provider: 'supabase',
    accessToken: session.access_token,
    access_token: session.access_token,
    refreshToken: session.refresh_token,
    refresh_token: session.refresh_token,
    expiresAt: session.expires_at,
    expires_at: session.expires_at,
    user,
  };
};

export class SupabaseAuthAdapter {
  async getSession(): Promise<SessionResult> {
    const { data, error } = await supabase.auth.getSession();
    return {
      data: { session: toAppSession(data.session) },
      error,
    };
  }

  async getUser(): Promise<UserResult> {
    const { data, error } = await supabase.auth.getUser();
    return {
      data: { user: toAppUser(data.user) },
      error,
    };
  }

  async getAccessToken(): Promise<string | null> {
    const { data } = await this.getSession();
    return data.session?.accessToken ?? null;
  }

  async signInWithPassword(args: {
    email: string;
    password: string;
  }): Promise<SignInWithPasswordResult> {
    const { data, error } = await supabase.auth.signInWithPassword(args);
    return {
      data: {
        session: toAppSession(data.session),
        user: toAppUser(data.user),
      },
      error,
    };
  }

  async signUp(args: {
    email: string;
    password: string;
    options?: {
      data?: AuthMetadata;
      emailRedirectTo?: string;
    };
  }): Promise<SignUpResult> {
    const { data, error } = await supabase.auth.signUp(args);
    return {
      data: {
        session: toAppSession(data.session),
        user: toAppUser(data.user),
      },
      error,
    };
  }

  async signInWithOAuth(args: {
    provider: OAuthProvider;
    options?: {
      redirectTo?: string;
      queryParams?: Record<string, string>;
    };
  }): Promise<{ error: Error | null }> {
    if (args.provider !== 'google' && args.provider !== 'azure') {
      return {
        error: new Error('This SSO provider requires Cognito authentication'),
      };
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: args.provider as Provider,
      options: args.options,
    });
    return { error };
  }

  async refreshSession(): Promise<SessionResult> {
    const { data, error } = await supabase.auth.refreshSession();
    return {
      data: { session: toAppSession(data.session) },
      error,
    };
  }

  async updateUser(attributes: {
    password?: string;
    data?: AuthMetadata;
  }): Promise<UserResult> {
    const { data, error } = await supabase.auth.updateUser(attributes);
    return {
      data: { user: toAppUser(data.user) },
      error,
    };
  }

  async resetPasswordForEmail(
    email: string,
    options?: { redirectTo?: string }
  ): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, options);
    return { error };
  }

  async confirmForgotPassword(): Promise<{ error: Error | null }> {
    return {
      error: new Error('Password reset confirmation is handled by Supabase'),
    };
  }

  async resend(args: {
    type: 'signup' | 'email_change';
    email: string;
    options?: { emailRedirectTo?: string };
  }): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.resend(args);
    return { error };
  }

  async signOut(options?: {
    scope?: 'global' | 'local' | 'others';
  }): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.signOut(options);
    return { error };
  }

  onAuthStateChange(
    callback: (event: AuthChangeEvent, session: AppAuthSession | null) => void
  ): { data: { subscription: AuthSubscription } } {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      callback(event, toAppSession(session));
    });
    return { data };
  }
}
